/**
 * Mermaid configuration with Shell branding
 * Provides theme configuration and initialization settings for Mermaid diagrams
 */

import type { MermaidConfig } from 'mermaid';

// Shell Brand Colors
export const SHELL_COLORS = {
  yellow: '#ffc600',     // Shell Yellow - Primary brand color
  red: '#dd1d21',        // Shell Red - Secondary brand color
  blue: '#336094',       // Shell Blue - Tertiary brand color
  white: '#ffffff',      // Background
  black: '#000000',      // Text
  lightGray: '#f8f9fa',  // Light background
  mediumGray: '#e9ecef', // Medium background
  darkGray: '#6c757d',   // Dark text
} as const;

// Responsive breakpoints
export const BREAKPOINTS = {
  MOBILE: 768,
  TABLET: 1024,
  DESKTOP: 1200,
} as const;

// Shell Mermaid Theme Configuration
export const shellMermaidTheme: MermaidConfig['themeVariables'] = {
  // Primary colors
  primaryColor: SHELL_COLORS.yellow,
  primaryTextColor: SHELL_COLORS.black,
  primaryBorderColor: SHELL_COLORS.red,
  
  // Secondary colors
  secondaryColor: SHELL_COLORS.lightGray,
  secondaryTextColor: SHELL_COLORS.black,
  secondaryBorderColor: SHELL_COLORS.blue,
  
  // Tertiary colors
  tertiaryColor: SHELL_COLORS.mediumGray,
  tertiaryTextColor: SHELL_COLORS.black,
  tertiaryBorderColor: SHELL_COLORS.darkGray,
  
  // Background and lines
  background: SHELL_COLORS.white,
  lineColor: SHELL_COLORS.blue,
  
  // Typography
  fontFamily: 'Shell Headline, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  fontSize: '14px',
  
  // Flowchart specific
  mainBkg: SHELL_COLORS.yellow,
  secondBkg: SHELL_COLORS.lightGray,
  
  // Sequence diagram specific
  actorBkg: SHELL_COLORS.yellow,
  actorBorder: SHELL_COLORS.red,
  actorTextColor: SHELL_COLORS.black,
  actorLineColor: SHELL_COLORS.blue,
  signalColor: SHELL_COLORS.black,
  signalTextColor: SHELL_COLORS.black,
  labelBoxBkgColor: SHELL_COLORS.lightGray,
  labelBoxBorderColor: SHELL_COLORS.blue,
  labelTextColor: SHELL_COLORS.black,
  loopTextColor: SHELL_COLORS.black,
  noteBkgColor: SHELL_COLORS.yellow,
  noteBorderColor: SHELL_COLORS.red,
  noteTextColor: SHELL_COLORS.black,
  
  // Gantt chart specific
  gridColor: SHELL_COLORS.blue,
  section0: SHELL_COLORS.yellow,
  section1: SHELL_COLORS.lightGray,
  section2: SHELL_COLORS.mediumGray,
  section3: SHELL_COLORS.yellow,
  
  // Class diagram specific
  classText: SHELL_COLORS.black,
  
  // Git graph specific
  git0: SHELL_COLORS.yellow,
  git1: SHELL_COLORS.red,
  git2: SHELL_COLORS.blue,
  git3: SHELL_COLORS.lightGray,
  git4: SHELL_COLORS.mediumGray,
  git5: SHELL_COLORS.darkGray,
  git6: SHELL_COLORS.yellow,
  git7: SHELL_COLORS.red,
  
  // State diagram specific
  labelColor: SHELL_COLORS.black,
  
  // Entity relationship diagram specific
  relationColor: SHELL_COLORS.blue,
  entityBorderColor: SHELL_COLORS.red,
  entityTextColor: SHELL_COLORS.black,
  attributeBackgroundColorOdd: SHELL_COLORS.lightGray,
  attributeBackgroundColorEven: SHELL_COLORS.white,
  
  // C4 diagram specific
  personBkg: SHELL_COLORS.yellow,
  personBorder: SHELL_COLORS.red,
  systemBkg: SHELL_COLORS.blue,
  systemBorder: SHELL_COLORS.red,
  systemDbBkg: SHELL_COLORS.lightGray,
  systemDbBorder: SHELL_COLORS.blue,
  systemQueueBkg: SHELL_COLORS.mediumGray,
  systemQueueBorder: SHELL_COLORS.blue,
  containerBkg: SHELL_COLORS.lightGray,
  containerBorder: SHELL_COLORS.blue,
  componentBkg: SHELL_COLORS.mediumGray,
  componentBorder: SHELL_COLORS.darkGray,
  relationLabelColor: SHELL_COLORS.black,
  relationLabelBackground: SHELL_COLORS.white,
  boundaryBkg: SHELL_COLORS.white,
  boundaryBorder: SHELL_COLORS.darkGray,
};

// Default Mermaid Configuration
export const defaultMermaidConfig: MermaidConfig = {
  theme: 'base',
  themeVariables: shellMermaidTheme,
  startOnLoad: false,
  securityLevel: 'loose',
  htmlLabels: true,
  // Add proper v10+ configuration
  deterministicIds: true,
  deterministicIDSeed: 'archie-mermaid',

  // Theme CSS variables for C4 diagrams
  themeCSS: `
    /* C4 Diagram Shell Branding */
    .c4Person > .c4Shape { fill: ${SHELL_COLORS.yellow}; stroke: ${SHELL_COLORS.red}; stroke-width: 2px; }
    .c4Person text { fill: ${SHELL_COLORS.black}; font-weight: 600; }
    .c4ExtPerson > .c4Shape { fill: ${SHELL_COLORS.lightGray}; stroke: ${SHELL_COLORS.red}; stroke-width: 2px; }
    .c4ExtPerson text { fill: ${SHELL_COLORS.black}; font-weight: 500; }
    .c4System > .c4Shape { fill: ${SHELL_COLORS.blue}; stroke: ${SHELL_COLORS.red}; stroke-width: 2px; }
    .c4System text { fill: ${SHELL_COLORS.white}; font-weight: 600; }
    .c4ExtSystem > .c4Shape { fill: ${SHELL_COLORS.mediumGray}; stroke: ${SHELL_COLORS.blue}; stroke-width: 2px; }
    .c4ExtSystem text { fill: ${SHELL_COLORS.black}; font-weight: 500; }
    .c4Container > .c4Shape { fill: ${SHELL_COLORS.lightGray}; stroke: ${SHELL_COLORS.blue}; stroke-width: 2px; }
    .c4Container text { fill: ${SHELL_COLORS.black}; font-weight: 500; }
    .c4Component > .c4Shape { fill: ${SHELL_COLORS.mediumGray}; stroke: ${SHELL_COLORS.darkGray}; stroke-width: 2px; }
    .c4Component text { fill: ${SHELL_COLORS.black}; font-weight: 500; }
    .c4Rel line { stroke: ${SHELL_COLORS.blue}; stroke-width: 2px; }
    .c4Rel polygon { fill: ${SHELL_COLORS.blue}; stroke: ${SHELL_COLORS.blue}; }
    .c4Rel text { fill: ${SHELL_COLORS.black}; font-size: 12px; font-weight: 500; }
    .c4Boundary rect {
      fill: rgba(255, 255, 255, 0.1);
      stroke: ${SHELL_COLORS.darkGray};
      stroke-width: 2px; stroke-dasharray: 5,5;
    }
    .c4Boundary text { fill: ${SHELL_COLORS.black}; font-weight: 700; font-size: 18px; }
    
    /* Responsive improvements */
    .mermaid {
      max-width: 100% !important;
      height: auto !important;
    }
    
    /* Make SVG responsive */
    .mermaid svg {
      max-width: 100% !important;
      height: auto !important;
      width: 100% !important;
    }
    
    /* Responsive text scaling */
    @media (min-width: 768px) {
      .c4Person text, .c4System text { font-size: 14px; }
      .c4Container text, .c4Component text { font-size: 13px; }
      .c4Boundary text { font-size: 20px; }
    }
    
    @media (min-width: 1024px) {
      .c4Person text, .c4System text { font-size: 16px; }
      .c4Container text, .c4Component text { font-size: 14px; }
      .c4Boundary text { font-size: 22px; }
    }
  `,
  flowchart: {
    useMaxWidth: true,
    htmlLabels: true,
    curve: 'basis',
    padding: 10,
  },
  sequence: {
    useMaxWidth: true,
    showSequenceNumbers: false,
    wrap: true,
    width: 150,
    height: 65,
    boxMargin: 10,
    boxTextMargin: 5,
    noteMargin: 10,
    messageMargin: 35,
    mirrorActors: true,
  },
  gantt: {
    useMaxWidth: true,
    leftPadding: 75,
    gridLineStartPadding: 35,
    fontSize: 11,
    sectionFontSize: 24,
    numberSectionStyles: 4,
  },
  journey: {
    useMaxWidth: true,
  },
  class: {
    useMaxWidth: true,
  },
  state: {
    useMaxWidth: true,
  },
  er: {
    useMaxWidth: true,
    diagramPadding: 20,
    layoutDirection: 'TB',
    minEntityWidth: 100,
    minEntityHeight: 75,
    entityPadding: 15,
    stroke: SHELL_COLORS.blue,
    fill: SHELL_COLORS.lightGray,
    fontSize: 12,
  },
  pie: {
    useMaxWidth: true,
  },
  requirement: {
    useMaxWidth: true,
  },
  gitGraph: {
    useMaxWidth: true,
    diagramPadding: 8,
    nodeLabel: {
      width: 75,
      height: 100,
      x: -25,
      y: -8,
    },
  },
  c4: {
    useMaxWidth: true,
    // Responsive diagram margins - smaller on mobile, larger on desktop
    diagramMarginX: 40,           // Reduced margin for better space utilization
    diagramMarginY: 20,           // Increased for better vertical spacing
    c4ShapeMargin: 50,            // Slightly reduced for better fit
    c4ShapeInRow: 4,              // Increased back to 4 for larger screens
    nextLinePaddingX: 15,         // Reduced padding for better space usage
    c4BoundaryInRow: 2,           // Allow 2 boundaries per row for better layout
    personFontSize: 16,           // Increased font size for better readability
    personFontFamily: shellMermaidTheme.fontFamily,
    personFontWeight: '600',      // Semi-bold for better visibility
    external_personFontSize: 15,  // Slightly smaller for external elements
    external_personFontFamily: shellMermaidTheme.fontFamily,
    external_personFontWeight: '500',
    systemFontSize: 16,           // Increased system font size
    systemFontFamily: shellMermaidTheme.fontFamily,
    systemFontWeight: '600',      // Semi-bold for systems
    external_systemFontSize: 15,  // External systems slightly smaller
    external_systemFontFamily: shellMermaidTheme.fontFamily,
    external_systemFontWeight: '500',
    containerFontSize: 14,        // Added container font configuration
    containerFontFamily: shellMermaidTheme.fontFamily,
    containerFontWeight: '500',
    componentFontSize: 13,        // Added component font configuration
    componentFontFamily: shellMermaidTheme.fontFamily,
    componentFontWeight: '500',
    boundaryFontSize: 18,         // Larger font for boundaries
    boundaryFontWeight: '700',    // Bold boundaries
    // Text wrapping and spacing
    wrap: true,
    wrapPadding: 10,
  },
};

// Dark theme variant for Shell branding
export const shellMermaidDarkTheme: MermaidConfig['themeVariables'] = {
  ...shellMermaidTheme,
  background: '#1a1a1a',
  primaryTextColor: SHELL_COLORS.white,
  secondaryTextColor: SHELL_COLORS.white,
  tertiaryTextColor: SHELL_COLORS.white,
  lineColor: SHELL_COLORS.yellow,
  signalColor: SHELL_COLORS.white,
  signalTextColor: SHELL_COLORS.white,
  labelTextColor: SHELL_COLORS.white,
  loopTextColor: SHELL_COLORS.white,
  noteTextColor: SHELL_COLORS.black, // Keep note text dark for contrast
  classText: SHELL_COLORS.white,
  labelColor: SHELL_COLORS.white,
  entityTextColor: SHELL_COLORS.white,
  
  // C4 diagram dark theme specific
  personBkg: SHELL_COLORS.yellow,
  personBorder: SHELL_COLORS.red,
  systemBkg: SHELL_COLORS.blue,
  systemBorder: SHELL_COLORS.yellow,
  systemDbBkg: SHELL_COLORS.darkGray,
  systemDbBorder: SHELL_COLORS.yellow,
  systemQueueBkg: SHELL_COLORS.mediumGray,
  systemQueueBorder: SHELL_COLORS.yellow,
  containerBkg: SHELL_COLORS.darkGray,
  containerBorder: SHELL_COLORS.yellow,
  componentBkg: SHELL_COLORS.mediumGray,
  componentBorder: SHELL_COLORS.white,
  relationLabelColor: SHELL_COLORS.white,
  relationLabelBackground: '#333333',
  boundaryBkg: '#2a2a2a',
  boundaryBorder: SHELL_COLORS.yellow,
};

// Configuration factory function
export const createMermaidConfig = (isDark = false): MermaidConfig => {
  const baseConfig = {
    ...defaultMermaidConfig,
    themeVariables: isDark ? shellMermaidDarkTheme : shellMermaidTheme,
  };
  
  // Add dark theme specific CSS for C4 diagrams
  if (isDark) {
    baseConfig.themeCSS = `
      /* C4 Diagram Shell Branding - Dark Theme */
      .c4Person > .c4Shape { fill: ${SHELL_COLORS.yellow}; stroke: ${SHELL_COLORS.red}; stroke-width: 2px; }
      .c4Person text { fill: ${SHELL_COLORS.black}; font-weight: 600; }
      .c4ExtPerson > .c4Shape { fill: ${SHELL_COLORS.darkGray}; stroke: ${SHELL_COLORS.yellow}; stroke-width: 2px; }
      .c4ExtPerson text { fill: ${SHELL_COLORS.white}; font-weight: 500; }
      .c4System > .c4Shape { fill: ${SHELL_COLORS.blue}; stroke: ${SHELL_COLORS.yellow}; stroke-width: 2px; }
      .c4System text { fill: ${SHELL_COLORS.white}; font-weight: 600; }
      .c4ExtSystem > .c4Shape { fill: ${SHELL_COLORS.mediumGray}; stroke: ${SHELL_COLORS.yellow}; stroke-width: 2px; }
      .c4ExtSystem text { fill: ${SHELL_COLORS.white}; font-weight: 500; }
      .c4Container > .c4Shape { fill: ${SHELL_COLORS.darkGray}; stroke: ${SHELL_COLORS.yellow}; stroke-width: 2px; }
      .c4Container text { fill: ${SHELL_COLORS.white}; font-weight: 500; }
      .c4Component > .c4Shape { fill: ${SHELL_COLORS.mediumGray}; stroke: ${SHELL_COLORS.white}; stroke-width: 2px; }
      .c4Component text { fill: ${SHELL_COLORS.white}; font-weight: 500; }
      .c4Rel line { stroke: ${SHELL_COLORS.yellow}; stroke-width: 2px; }
      .c4Rel polygon { fill: ${SHELL_COLORS.yellow}; stroke: ${SHELL_COLORS.yellow}; }
      .c4Rel text { fill: ${SHELL_COLORS.white}; font-size: 12px; font-weight: 500; }
      .c4Boundary rect {
        fill: rgba(0, 0, 0, 0.2); stroke: ${SHELL_COLORS.yellow};
        stroke-width: 2px; stroke-dasharray: 5,5;
      }
      .c4Boundary text { fill: ${SHELL_COLORS.white}; font-weight: 700; font-size: 18px; }
    `;
  }
  
  return baseConfig;
};

// Utility function to get diagram type from mermaid code
export const getMermaidDiagramType = (code: string): string => {
  const trimmedCode = code.trim();
  
  if (trimmedCode.startsWith('graph') || trimmedCode.startsWith('flowchart')) {
    return 'flowchart';
  } else if (trimmedCode.startsWith('sequenceDiagram')) {
    return 'sequence';
  } else if (trimmedCode.startsWith('gantt')) {
    return 'gantt';
  } else if (trimmedCode.startsWith('classDiagram')) {
    return 'class';
  } else if (trimmedCode.startsWith('stateDiagram')) {
    return 'state';
  } else if (trimmedCode.startsWith('erDiagram')) {
    return 'er';
  } else if (trimmedCode.startsWith('journey')) {
    return 'journey';
  } else if (trimmedCode.startsWith('pie')) {
    return 'pie';
  } else if (trimmedCode.startsWith('quadrantChart')) {
    return 'quadrantChart';
  } else if (trimmedCode.startsWith('requirementDiagram')) {
    return 'requirement';
  } else if (trimmedCode.startsWith('gitGraph')) {
    return 'gitGraph';
  } else if (trimmedCode.startsWith('C4Context') || trimmedCode.startsWith('C4Container') || trimmedCode.startsWith('C4Component')) {
    return 'c4';
  }
  
  return 'unknown';
};

// Validate mermaid syntax (basic validation)
export const validateMermaidSyntax = (code: string): { isValid: boolean; error?: string } => {
  if (!code || code.trim().length === 0) {
    return { isValid: false, error: 'Empty diagram code' };
  }
  
  const diagramType = getMermaidDiagramType(code);
  if (diagramType === 'unknown') {
    return { isValid: false, error: 'Unknown diagram type. Please start with a valid diagram declaration.' };
  }
  
  // Basic syntax checks
  const lines = code.split('\n').map(line => line.trim()).filter(line => line.length > 0);
  if (lines.length < 2) {
    return { isValid: false, error: 'Diagram must contain at least one element' };
  }
  
  return { isValid: true };
};

// Create responsive Mermaid configuration based on screen size
export const createResponsiveMermaidConfig = (
  isDark = false, 
  screenWidth?: number,
): MermaidConfig => {
  const baseConfig = createMermaidConfig(isDark);
  
  // Detect screen size if not provided
  const width = screenWidth ?? (globalThis.window === undefined ? 1024 : globalThis.window.innerWidth);
  
  // Adjust C4 configuration based on screen size
  if (baseConfig.c4) {
    if (width < BREAKPOINTS.MOBILE) {
      // Mobile: Fewer items per row, smaller margins
      baseConfig.c4 = {
        ...baseConfig.c4,
        c4ShapeInRow: 2,
        c4BoundaryInRow: 1,
        diagramMarginX: 20,
        c4ShapeMargin: 30,
        nextLinePaddingX: 10,
        personFontSize: 14,
        systemFontSize: 14,
        containerFontSize: 12,
        componentFontSize: 11,
        boundaryFontSize: 16,
      };
    } else if (width < BREAKPOINTS.TABLET) {
      // Tablet: Medium density
      baseConfig.c4 = {
        ...baseConfig.c4,
        c4ShapeInRow: 3,
        c4BoundaryInRow: 1,
        diagramMarginX: 30,
        c4ShapeMargin: 40,
        nextLinePaddingX: 12,
        personFontSize: 15,
        systemFontSize: 15,
        containerFontSize: 13,
        componentFontSize: 12,
        boundaryFontSize: 18,
      };
    } else {
      // Desktop: Full density
      baseConfig.c4 = {
        ...baseConfig.c4,
        c4ShapeInRow: 4,
        c4BoundaryInRow: 2,
        diagramMarginX: 40,
        c4ShapeMargin: 50,
        nextLinePaddingX: 15,
        personFontSize: 16,
        systemFontSize: 16,
        containerFontSize: 14,
        componentFontSize: 13,
        boundaryFontSize: 20,
      };
    }
  }
  
  return baseConfig;
};
